import { Ref } from "react";
import {
  CreatorFragmentCommonProps,
  CreatorFragmentHandle,
} from "@coworker/apprestructured/src/task-management/components/TaskCreatorAndEditorSheet/creator-fragments/CreatorFragment";
import { isLoading } from "@coworker/apprestructured/src/task-management/loading";
import LoadingCreatorFragment from "@coworker/apprestructured/src/task-management/components/LoadingCreatorFragment/LoadingCreatorFragment";
import { getByPath } from "@coworker/apprestructured/src/shared/helpers/object.helper";
import { validateInitialDetailsFieldValue } from "@coworker/apprestructured/src/shared/helpers/schemaValidator";
import { useTranslation } from "@coworker/locales";
import TitledTextWithModal from "@coworker/apprestructured/src/task-management/components/TitledTextWithModal/TitledTextWithModal";
import { ItemNoAndType } from "@coworker/apprestructured/src/task-management/types/TaskDetailTypes";
import { useMediasAndArticleCount } from "@coworker/app/src/components/RoomSettings/hooks/useMediasAndArticleCount";
import { useStoreId } from "@coworker/app/src/core/auth/useLoggedInUser";
import { MediaPlacementsListView } from "@coworker/apprestructured/src/media-maintenance/components/MediaPlacementSection/MediaPlacementsModal/MediaPlacementsListView";
import { MediasAndArticleCountResponse } from "@coworker/app/src/components/RoomSettings/types/media";

export type MediasLocationCreatorFragmentConfigProps = {
  field: string;
  title: string;
};

type MediasLocationCreatorFragmentProps = {
  configProps: MediasLocationCreatorFragmentConfigProps;
  commonProps: CreatorFragmentCommonProps;
  ref?: Ref<CreatorFragmentHandle>;
};

type MediaPieces = {
  id: string;
  pieces: number;
  article_id: string;
};

function MediasLocationCreatorFragment({
  configProps,
  commonProps,
}: MediasLocationCreatorFragmentProps) {
  const storeId = useStoreId();
  const { field, title } = configProps;
  const { task, taskType } = commonProps;
  const { t } = useTranslation();

  const mediaPieces = validateInitialDetailsFieldValue<MediaPieces[]>(
    getByPath(task, field),
    field,
    taskType
  );

  const itemNoAndType = validateInitialDetailsFieldValue<ItemNoAndType>(
    getByPath(task, "details.item"),
    "details.item",
    taskType
  );

  const { data: mediasAndArticleCount } = useMediasAndArticleCount(
    isLoading(itemNoAndType) ? "" : itemNoAndType?.no,
    storeId
  );

  if (isLoading(mediaPieces) || isLoading(itemNoAndType)) {
    return <LoadingCreatorFragment />;
  }

  if (mediaPieces === null) {
    return null;
  }

  const selectedMedias = mediasAndArticleCount?.filter(
    (media) =>
      mediaPieces.some(
        (piece) =>
          piece.id === media.mediaId && piece.article_id === media.articleId
      ) && !media.parentArticleCombinationNumber
  );

  const groupByMediaType: Record<string, MediasAndArticleCountResponse[]> =
    selectedMedias?.reduce(
      (acc, media) => {
        const type = media.mediaType || "OTHER";
        if (!acc[type]) {
          acc[type] = [];
        }
        acc[type].push(media);
        return acc;
      },
      {} as Record<string, MediasAndArticleCountResponse[]>
    ) ?? {};

  // Count unique medias (same mediaId can appear multiple times with different articles)
  const uniqueMediaCount = new Set(
    selectedMedias?.map((media) => media.mediaId)
  ).size;

  return (
    <TitledTextWithModal
      title={t(title) ?? "Location"}
      description={`${uniqueMediaCount} Medias`}
      modalContent={
        <MediaPlacementsListView
          groupedMediasByType={groupByMediaType}
          showCheckBox={false}
        />
      }
    />
  );
}

export default MediasLocationCreatorFragment;
